/**
 * SRES · Shared 3D Scene Coordinate Service (Feb 2026)
 * ═══════════════════════════════════════════════════
 *
 * A single source of truth for converting mining/geological world coordinates
 * (Easting / Northing / Elevation, typically UTM) into local Three.js scene
 * coordinates with the standard mining-viewer axis convention:
 *
 *     scene.x =    (Easting     - origin.easting)
 *     scene.y =    (Elevation   - origin.elevation)    · verticalExaggeration
 *     scene.z = -  (Northing    - origin.northing)
 *
 * Rationale:
 *   · UTM eastings/northings are in the 100 000–10 000 000 metre range.
 *     Passing those into Three.js verbatim collapses the entire scene to a
 *     single pixel near ±infinity — this is the well-documented cause of
 *     the "block model renders as a tiny dot" bug (P0, Feb 2026).
 *   · Every layer (blocks, drillholes, mine design, geo mesh, assay discs,
 *     section planes, pit shells, wireframes, topography) MUST use these
 *     helpers so origins, axis order and vertical exaggeration are always
 *     consistent.
 *   · The scene origin should be computed ONCE per (project, estimation_run)
 *     and remain stable across cut-off, filter, layer or camera changes.
 *
 * COORDINATE-SPACE METADATA
 *   Any geometry passed to `worldToScene` must be in `world_utm` space.
 *   Data already in `local_scene` (e.g. a mesh whose vertices have been
 *   pre-transformed by the backend) must be marked as such and skipped by
 *   `worldToScene`. Screen-space overlays (compass, gizmo, scale bar) must
 *   never be transformed.
 */
import * as THREE from "three";


/** Coordinate-space tags. */
export const COORD_SPACE = {
  WORLD_UTM:     "world_utm",
  LOCAL_SCENE:   "local_scene",
  SCREEN_OVERLAY: "screen_overlay",
};


/**
 * Convert (easting, northing, elevation) → THREE.Vector3 in local scene space.
 *
 * @param {number} easting
 * @param {number} northing
 * @param {number} elevation
 * @param {{easting:number, northing:number, elevation:number}} origin
 * @param {number} [verticalExaggeration=1] — applies only to Y (elevation).
 * @returns {THREE.Vector3}
 */
export function worldToScene(easting, northing, elevation, origin, verticalExaggeration = 1) {
  const oe = origin?.easting   ?? 0;
  const on = origin?.northing  ?? 0;
  const oz = origin?.elevation ?? 0;
  return new THREE.Vector3(
    easting - oe,
    (elevation - oz) * verticalExaggeration,
    -(northing - on),
  );
}


/**
 * Inverse of worldToScene — recover (easting, northing, elevation) from a
 * local scene vector. Used for coordinate readouts and export.
 */
export function sceneToWorld(vector, origin, verticalExaggeration = 1) {
  return {
    easting:   vector.x + (origin?.easting   ?? 0),
    northing: -vector.z + (origin?.northing  ?? 0),
    elevation: (vector.y / (verticalExaggeration || 1)) + (origin?.elevation ?? 0),
  };
}


/** Reject NaN, Infinity or values so extreme they can only be junk. */
export function isValidCoordinate(x, y, z) {
  return (
    Number.isFinite(x) &&
    Number.isFinite(y) &&
    Number.isFinite(z) &&
    Math.abs(x) < 1e8 &&
    Math.abs(y) < 1e8 &&
    Math.abs(z) < 1e6
  );
}


/**
 * Compute a stable scene origin from a set of {x=E, y=N, z=Elev} points.
 * Returns null if no valid points.
 */
export function computeSceneOriginFromPoints(points) {
  if (!points?.length) return null;
  let xMin = +Infinity, xMax = -Infinity;
  let yMin = +Infinity, yMax = -Infinity;
  let zMin = +Infinity, zMax = -Infinity;
  let n = 0;
  for (const p of points) {
    const x = Number(p?.x), y = Number(p?.y), z = Number(p?.z);
    if (!isValidCoordinate(x, y, z)) continue;
    if (x < xMin) xMin = x; if (x > xMax) xMax = x;
    if (y < yMin) yMin = y; if (y > yMax) yMax = y;
    if (z < zMin) zMin = z; if (z > zMax) zMax = z;
    n++;
  }
  if (!n) return null;
  return {
    easting:   (xMin + xMax) / 2,
    northing:  (yMin + yMax) / 2,
    elevation: (zMin + zMax) / 2,
    bbox: { xMin, xMax, yMin, yMax, zMin, zMax },
    validCount: n,
  };
}


/**
 * Compute an authoritative local-scene Box3 from a list of world-UTM points
 * (each with optional half-size vector). Skips invalid coords.
 *
 * @param {Array<{x,y,z,dx?,dy?,dz?}>} items — world_utm points
 * @param {{easting,northing,elevation}} origin
 * @param {number} verticalExaggeration
 * @returns {{box: THREE.Box3, rejected: number}}
 */
export function computeLocalBoundingBox(items, origin, verticalExaggeration = 1) {
  const box = new THREE.Box3();
  let rejected = 0;
  const tmp = new THREE.Vector3();
  const halfLo = new THREE.Vector3();
  const halfHi = new THREE.Vector3();
  for (const it of items || []) {
    const x = Number(it?.x), y = Number(it?.y), z = Number(it?.z);
    if (!isValidCoordinate(x, y, z)) { rejected++; continue; }
    tmp.set(
      x - origin.easting,
      (z - origin.elevation) * verticalExaggeration,
      -(y - origin.northing),
    );
    const dx = Number(it?.dx || 0);
    const dy = Number(it?.dy || 0);
    const dz = Number(it?.dz || 0) * verticalExaggeration;
    if (dx || dy || dz) {
      halfLo.set(tmp.x - dx / 2, tmp.y - dz / 2, tmp.z - dy / 2);
      halfHi.set(tmp.x + dx / 2, tmp.y + dz / 2, tmp.z + dy / 2);
      box.expandByPoint(halfLo);
      box.expandByPoint(halfHi);
    } else {
      box.expandByPoint(tmp);
    }
  }
  return { box, rejected };
}


/**
 * Compute a perspective-camera distance that frames a bounding box for a
 * given camera FOV & aspect. Uses both vertical & horizontal FOV, then picks
 * the larger required distance with padding.
 *
 * @param {THREE.Box3} box (local scene coords)
 * @param {{fov:number, aspect:number}} camera
 * @param {number} [padding=1.35]
 */
export function fitDistanceForBox(box, camera, padding = 1.35) {
  if (!box || box.isEmpty()) return null;
  const size = box.getSize(new THREE.Vector3());
  const maxSize = Math.max(size.x, size.y, size.z);
  if (!Number.isFinite(maxSize) || maxSize <= 0) return null;

  const verticalFov = THREE.MathUtils.degToRad(camera.fov || 45);
  const horizontalFov = 2 * Math.atan(Math.tan(verticalFov / 2) * (camera.aspect || 1));

  const distanceV = size.y / (2 * Math.tan(verticalFov / 2));
  const horizontalSize = Math.max(size.x, size.z);
  const distanceH = horizontalSize / (2 * Math.tan(horizontalFov / 2));

  const distance = Math.max(distanceV, distanceH, maxSize) * padding;
  return {
    distance,
    center: box.getCenter(new THREE.Vector3()),
    size,
    maxSize,
    near: Math.max(distance / 10000, 0.1),
    far:  Math.max(distance * 100, 10000),
  };
}


/** Six canonical view directions plus isometrics. */
export const VIEW_DIRECTIONS = {
  iso_ne:  new THREE.Vector3( 1, 0.75,  1).normalize(),
  iso_sw:  new THREE.Vector3(-1, 0.75, -1).normalize(),
  top:     new THREE.Vector3( 0, 1,     0),
  front:   new THREE.Vector3( 0, 0.05,  1).normalize(),
  east:    new THREE.Vector3( 1, 0.05,  0).normalize(),
  north:   new THREE.Vector3( 0, 0.05, -1).normalize(),
};


/**
 * Compute a "buffered active-model bbox" around a block-set bbox. Used to
 * decide which drillholes count as "in the active model" during Fit Active
 * Model — regional outlier holes stay excluded from the default frame.
 */
export function bufferedBBox(blockBox, bufferFraction = 0.25) {
  if (!blockBox || blockBox.isEmpty()) return null;
  const size = blockBox.getSize(new THREE.Vector3());
  const modelSpan = Math.max(size.x, size.z);   // horizontal in scene space
  const buffer = modelSpan * bufferFraction;
  const min = blockBox.min.clone().sub(new THREE.Vector3(buffer, buffer, buffer));
  const max = blockBox.max.clone().add(new THREE.Vector3(buffer, buffer, buffer));
  return new THREE.Box3(min, max);
}


/** Test whether a local-scene point sits inside a bbox. */
export function pointInBox(vec, box) {
  return (
    vec.x >= box.min.x && vec.x <= box.max.x &&
    vec.y >= box.min.y && vec.y <= box.max.y &&
    vec.z >= box.min.z && vec.z <= box.max.z
  );
}
